import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, MessageSquareText, ChevronDown } from "lucide-react";
import { EASE } from "./Reveal";

const SPHERE_NODES = [
    { label: "Desk Booking", x: 10, y: 20 },
    { label: "Floor Plans", x: 82, y: 12 },
    { label: "Audit Trail", x: 90, y: 55 },
    { label: "Car Logistics", x: 72, y: 88 },
    { label: "People", x: 18, y: 84 },
    { label: "AI Assistant", x: 4, y: 52 },
];

function MaskedLine({ children, delay, testid }) {
    const reduce = useReducedMotion();
    return (
        <span className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
            <motion.span
                data-testid={testid}
                className="block will-change-transform"
                initial={reduce ? { opacity: 0 } : { y: "112%" }}
                animate={reduce ? { opacity: 1 } : { y: 0 }}
                transition={{ duration: 0.95, delay, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

function ParticleSphere() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;
        const ctx = canvas.getContext("2d");
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const size = canvas.clientWidth || 560;
        canvas.width = size * dpr;
        canvas.height = size * dpr;
        ctx.scale(dpr, dpr);

        const N = 650;
        const pts = [];
        for (let i = 0; i < N; i += 1) {
            const phi = Math.acos(1 - (2 * (i + 0.5)) / N);
            const theta = Math.PI * (1 + Math.sqrt(5)) * i;
            pts.push({
                x: Math.sin(phi) * Math.cos(theta),
                y: Math.cos(phi),
                z: Math.sin(phi) * Math.sin(theta),
                cool: i % 14 === 0,
                ox: 0,
                oy: 0,
                p: Math.random() * 6.2832,
            });
        }

        const mouse = { x: -9999, y: -9999 };
        const onMove = (e) => {
            const r = canvas.getBoundingClientRect();
            mouse.x = e.clientX - r.left;
            mouse.y = e.clientY - r.top;
        };
        if (!reduce) window.addEventListener("pointermove", onMove, { passive: true });

        let t = 0.6;
        let f = 0;
        let raf;
        const tilt = 0.32;
        const cTilt = Math.cos(tilt);
        const sTilt = Math.sin(tilt);

        const draw = () => {
            ctx.clearRect(0, 0, size, size);
            const R = size * 0.38;
            const cx = size / 2;
            const cy = size / 2;
            const ct = Math.cos(t);
            const st = Math.sin(t);

            const glow = ctx.createRadialGradient(cx, cy, R * 0.1, cx, cy, R * 1.55);
            glow.addColorStop(0, "rgba(246, 228, 170, 0.12)");
            glow.addColorStop(1, "rgba(246, 228, 170, 0)");
            ctx.fillStyle = glow;
            ctx.fillRect(0, 0, size, size);
            ctx.globalCompositeOperation = "lighter";

            for (let i = 0; i < N; i += 1) {
                const p = pts[i];
                const x = p.x * ct - p.z * st;
                let z = p.x * st + p.z * ct;
                const y = p.y * cTilt - z * sTilt;
                z = p.y * sTilt + z * cTilt;
                const depth = (z + 1) / 2;
                const bx = cx + x * R;
                const by = cy + y * R;
                const ddx = bx + p.ox - mouse.x;
                const ddy = by + p.oy - mouse.y;
                const d2 = ddx * ddx + ddy * ddy;
                if (d2 < 10000) {
                    const d = Math.sqrt(d2) || 1;
                    const fr = (1 - d / 100) * 3.4;
                    p.ox += (ddx / d) * fr;
                    p.oy += (ddy / d) * fr;
                }
                p.ox *= 0.9;
                p.oy *= 0.9;
                const tw = 0.5 + 0.5 * Math.sin(f * 0.045 + p.p);
                const a = (0.10 + depth * 0.75) * (0.5 + 0.5 * tw);
                ctx.fillStyle = p.cool
                    ? `rgba(240, 217, 140, ${a})`
                    : `rgba(250, 245, 232, ${a})`;
                ctx.beginPath();
                ctx.arc(bx + p.ox, by + p.oy, 0.5 + depth * (1.2 + tw * 0.9), 0, 6.2832);
                ctx.fill();
            }
            ctx.globalCompositeOperation = "source-over";
            if (!reduce) {
                t += 0.0024;
                f += 1;
                raf = requestAnimationFrame(draw);
            }
        };
        draw();
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("pointermove", onMove);
        };
    }, []);

    return <canvas ref={canvasRef} data-testid="hero-particle-sphere" className="w-full h-full" aria-hidden="true" />;
}

export function Hero() {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const { scrollY } = useScroll();
    const yDots = useTransform(scrollY, [0, 900], [0, reduce ? 0 : 420]);
    const yPlan = useTransform(scrollY, [0, 900], [0, reduce ? 0 : 260]);

    return (
        <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-center overflow-hidden">
            <motion.div style={{ y: yDots }} className="dot-grid absolute -top-[25%] left-0 right-0 h-[150%] opacity-70 pointer-events-none" />
            <motion.div
                style={{ y: yPlan }}
                className="absolute z-20 right-[-30%] sm:right-[-6%] lg:right-[0%] top-1/2 -translate-y-1/2 w-[440px] sm:w-[620px] lg:w-[580px] xl:w-[680px] aspect-square opacity-70 sm:opacity-90 pointer-events-none"
            >
                <ParticleSphere />
                <svg className="absolute inset-0 hidden lg:block w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                    {SPHERE_NODES.map((n) => (
                        <line key={n.label} x1={n.x} y1={n.y} x2="50" y2="50" stroke="#D4A93C" strokeOpacity="0.16" strokeWidth="0.18" strokeDasharray="1.6 1.6" />
                    ))}
                </svg>
                {SPHERE_NODES.map((n) => (
                    <button
                        key={n.label}
                        type="button"
                        data-testid={`hero-node-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                        aria-label={n.label}
                        className="group absolute hidden lg:flex pointer-events-auto min-w-[44px] min-h-[44px] items-center justify-center"
                        style={{ left: `${n.x}%`, top: `${n.y}%`, transform: "translate(-50%, -50%)" }}
                    >
                        <span className="absolute w-2.5 h-2.5 rounded-full bg-[var(--accent)] shadow-[0_0_12px_rgba(212,169,60,0.8)]" />
                        <span className="absolute w-2.5 h-2.5 rounded-full bg-[var(--accent)]/50 animate-ping" />
                        <span
                            className={`absolute ${n.x > 55 ? "right-6" : "left-6"} whitespace-nowrap mono text-[10px] tracking-[0.18em] uppercase text-[var(--gold)] bg-[#0A0908]/85 border border-[var(--line-strong)] rounded px-2 py-1 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 group-focus-visible:opacity-100 group-focus-visible:translate-y-0 transition-[opacity,transform] duration-300`}
                        >
                            {n.label}
                        </span>
                    </button>
                ))}
            </motion.div>


            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-24 pb-20">
                <motion.p
                    className="mono text-[11px] sm:text-xs tracking-[0.3em] text-[var(--accent)] font-medium uppercase mb-6 flex items-center gap-3"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.15 }}
                >
                    <span className="inline-block w-8 h-px bg-[var(--accent)]" />
                    18.5204° N, 73.8567° E — IDeaS India · Pune
                </motion.p>

                <h1
                    data-testid="hero-main-headline"
                    className="font-display font-extrabold tracking-[-0.03em] leading-[1.04] text-[2.4rem] sm:text-6xl lg:text-7xl text-[var(--ink)] max-w-3xl"
                >
                    <MaskedLine delay={0.25}>The IDeaS India Office,</MaskedLine>
                    <MaskedLine delay={0.4}>
                        In <span className="foil-text">One Place.</span>
                    </MaskedLine>
                </h1>

                <motion.p
                    data-testid="hero-subheadline"
                    className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[var(--ink-2)]"
                    initial={{ opacity: 0, y: reduce ? 0 : 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.65, ease: EASE }}
                >
                    From desk bookings to car requests, floor plans to Finance &amp; Admin queries — IDeaSpace handles the
                    daily operations of the IDeaS Pune office.
                </motion.p>

                <motion.div
                    className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4"
                    initial={{ opacity: 0, y: reduce ? 0 : 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.8, ease: EASE }}
                >
                    <a
                        href="/app"
                        data-testid="hero-cta-open-app"
                        className="group min-h-[48px] px-7 inline-flex items-center justify-center gap-2 rounded-full bg-[var(--accent)] text-[#0A0908] font-semibold text-base shadow-[0_10px_30px_-10px_rgba(235,66,3,0.4)] hover:bg-[var(--accent-hover)] hover:-translate-y-[2px] active:translate-y-0 transition-[transform,background-color,box-shadow] duration-200"
                    >
                        Open IDeaSpace
                        <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
                    </a>
                    <a
                        href="/chatbot"
                        data-testid="hero-cta-try-ai"
                        className="group min-h-[48px] px-7 inline-flex items-center justify-center gap-2 rounded-full bg-[var(--paper)]/80 backdrop-blur border border-[var(--line-strong)] text-[var(--ink)] font-semibold text-base hover:border-[var(--accent)] hover:text-[var(--accent)] hover:-translate-y-[2px] active:translate-y-0 transition-[transform,color,border-color] duration-200"
                    >
                        <MessageSquareText size={18} />
                        Try the AI Assistant
                    </a>
                </motion.div>
            </div>

            <motion.div
                className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-[var(--ink-3)]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 0.8 }}
                aria-hidden="true"
            >
                <span className="mono text-[10px] tracking-[0.3em] uppercase">Scroll</span>
                <motion.span animate={reduce ? {} : { y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
                    <ChevronDown size={16} />
                </motion.span>
            </motion.div>
        </section>
    );
}
